import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../AuthContext'
import { adDetectionApi, bettingApi, reportApi } from '../api'
import { COUNTRY_CODES } from '../types'
import { enumLabel, LanguageSwitcher, useI18n, type MessageKey, type TFunction } from '../i18n'
import { errorMessage } from '../components/ErrorState'
import { Icon } from './icons'
import { navigate } from './router'
import { PERIOD_OPTIONS, useShell } from './ShellContext'
import { useTheme } from './theme'
import type { ThemePreference } from './theme'
import type { BadgeKey, NavItem } from './navigation'
import { canAccess, findItem, findSub, isManagerRole, NAVIGATION, visibleNavigation } from './navigation'
import './shell.css'
import './shell-collapse.css'

export interface ScreenScopes {
  /** L'écran filtre par pays : le sélecteur est affiché. */
  country?: boolean
  /** L'écran filtre par période : le sélecteur est affiché. */
  period?: boolean
}

interface Props {
  path: string[]
  scopes: ScreenScopes
  children: React.ReactNode
}

/** Compteurs affichés dans le menu. Rafraîchis à la minute, et jamais inventés. */
function useNavBadges(enabled: { ads: boolean; betting: boolean }) {
  const [badges, setBadges] = useState<Partial<Record<BadgeKey, number>>>({})

  const load = useCallback(async () => {
    const next: Partial<Record<BadgeKey, number>> = {}
    await Promise.allSettled([
      enabled.betting
        ? bettingApi.summary({}).then((s) => {
            next['betting.tests'] = s.open
            next['betting.qualify'] = s.pendingCandidates
          })
        : Promise.resolve(),
      enabled.ads
        ? adDetectionApi.getStats().then((s) => {
            next['ads.advertisers'] = (s.byVerification?.NEW ?? 0) + (s.byVerification?.TO_REVIEW ?? 0)
          })
        : Promise.resolve(),
    ])
    setBadges(next)
  }, [enabled.ads, enabled.betting])

  useEffect(() => {
    void load()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, 60_000)
    return () => clearInterval(timer)
  }, [load])

  return badges
}

export function AppShell({ path, scopes, children }: Props) {
  const { user, logout, isSuperAdmin, orgs, scopedOrgId, scopedOrg, setScopedOrg, effectiveCountries } = useAuth()
  const { t, lang } = useI18n()
  const { country, setCountry, period, setPeriod, crumb } = useShell()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const { preference, theme, setPreference } = useTheme()
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const sections = useMemo(() => visibleNavigation(user), [user])
  const current = findItem(path)
  const currentSub = current ? findSub(current, path) : null

  const allowed = (key: string) => {
    const item = NAVIGATION.flatMap((s) => s.items).find((i) => i.permission === key)
    return item ? canAccess(user, item) : false
  }
  const badges = useNavBadges({ ads: allowed('ads'), betting: allowed('betting') })

  // Le menu mobile se referme dès qu'on change d'écran.
  useEffect(() => setMobileOpen(false), [path.join('/')])

  // Un pays mémorisé hors du périmètre (changement d'organisation) est oublié.
  const countryOptions = useMemo(() => {
    const codes: string[] = effectiveCountries ?? [...COUNTRY_CODES]
    return codes.map((c) => ({ code: c, label: enumLabel(t, 'country', c, c) }))
  }, [effectiveCountries, t])
  useEffect(() => {
    if (country && !countryOptions.some((o) => o.code === country)) setCountry('')
  }, [country, countryOptions, setCountry])

  const exportPortfolio = async () => {
    setExporting(true)
    setExportError(null)
    try {
      await reportApi.downloadPortfolioPdf()
    } catch (err) {
      setExportError(errorMessage(err))
    } finally {
      setExporting(false)
    }
  }

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      writeCollapsed(!v)
      return !v
    })
  }

  if (!user) return null
  const initials = user.fullName
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const crumbs: Array<{ label: string; path?: string }> = []
  if (current) {
    const section = NAVIGATION.find((s) => s.items.includes(current))
    if (current.subs) {
      crumbs.push({ label: t(current.labelKey), path: current.path })
      if (currentSub && currentSub.path !== current.path) crumbs.push({ label: t(currentSub.labelKey), path: currentSub.path })
      else if (!crumb) crumbs.push({ label: t(current.subs[0].labelKey) })
    } else {
      // Une section au même nom que son unique entrée répéterait le fil d'Ariane.
      if (section?.titleKey && section.titleKey !== current.labelKey) crumbs.push({ label: t(section.titleKey) })
      crumbs.push({ label: t(current.labelKey), path: current.path })
    }
  }
  if (crumb) crumbs.push({ label: crumb })

  return (
    <div className={`shell${mobileOpen ? ' shell-nav-open' : ''}${collapsed ? ' shell-collapsed' : ''}`}>
      <aside className="shell-sidebar" aria-label={t('shell.nav')}>
        <div className="shell-brand-row">
          <a className="shell-brand" href="#/overview" title={collapsed ? t('login.product') : undefined}>
            <span className="shell-brand-mark" aria-hidden="true">IT</span>
            <span className="shell-brand-text">
              <strong>{t('login.product')}</strong>
              <span>{scopedOrg?.name ?? 'InTouch'}</span>
            </span>
          </a>
          <button
            type="button"
            className="shell-icon-btn shell-collapse-btn"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('shell.expandMenu') : t('shell.collapseMenu')}
            title={collapsed ? t('shell.expandMenu') : t('shell.collapseMenu')}
          >
            <Icon name="sidebar" />
          </button>
        </div>

        <nav className="shell-nav">
          {sections.map((section) => (
            <div key={section.titleKey ?? 'root'} className="shell-nav-group">
              {section.titleKey && <div className="nav-section">{t(section.titleKey)}</div>}
              {section.items.map((item) => (
                <NavEntry
                  key={item.path}
                  item={item}
                  active={item === current}
                  activeSubPath={currentSub?.path}
                  badges={badges}
                  collapsed={collapsed}
                  t={t}
                />
              ))}
            </div>
          ))}
        </nav>

        <ThemeSwitch preference={preference} theme={theme} onChange={setPreference} collapsed={collapsed} t={t} />

        {/* Le choix de langue vit au bas du menu, à côté du thème : deux
            réglages d'affichage, au même endroit avant et après connexion. */}
        <div className="shell-lang">
          <LanguageSwitcher variant={collapsed ? 'compact' : 'segmented'} />
        </div>

        <div className="shell-user">
          <span className="shell-avatar" aria-hidden="true" title={collapsed ? `${user.fullName} · ${enumLabel(t, 'role', user.role, user.role)}` : undefined}>{initials}</span>
          <span className="shell-user-text">
            <strong title={user.email}>{user.fullName}</strong>
            <span>{enumLabel(t, 'role', user.role, user.role)}</span>
          </span>
          <button type="button" className="shell-icon-btn" onClick={logout} title={t('shell.logout')} aria-label={t('shell.logout')}>
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      <div className="shell-scrim" aria-hidden="true" onClick={() => setMobileOpen(false)} />

      <div className="shell-main">
        <header className="shell-topbar">
          <button type="button" className="shell-icon-btn shell-menu-btn" aria-label={t('shell.openMenu')} onClick={() => setMobileOpen(true)}>
            <Icon name="menu" size={18} />
          </button>
          <nav className="shell-crumbs" aria-label={t('shell.breadcrumb')}>
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1
              return (
                <span key={`${c.label}-${i}`} className="shell-crumb">
                  {i > 0 && <span className="shell-crumb-sep" aria-hidden="true">/</span>}
                  {c.path && !last ? (
                    <a href={`#/${c.path}`}>{c.label}</a>
                  ) : (
                    <span aria-current={last ? 'page' : undefined} className={last ? 'shell-crumb-current' : undefined}>
                      {c.label}
                    </span>
                  )}
                </span>
              )
            })}
          </nav>

          <div className="shell-controls">
            {isSuperAdmin && (
              <label className="shell-select">
                <span className="sr-only">{t('common.organization')}</span>
                <select value={scopedOrgId ?? ''} onChange={(e) => setScopedOrg(e.target.value || null)} title={t('shell.viewOrgData')}>
                  <option value="">{t('shell.allOrgs')}</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </label>
            )}
            {scopes.country && (
              <label className="shell-select">
                <span className="sr-only">{t('common.country')}</span>
                <select value={country} onChange={(e) => setCountry(e.target.value)}>
                  <option value="">{t('common.allCountries')}</option>
                  {countryOptions.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </label>
            )}
            {scopes.period && (
              <label className="shell-select">
                <span className="sr-only">{t('common.period')}</span>
                <select value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
                  {PERIOD_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{t(p.labelKey)}</option>
                  ))}
                </select>
              </label>
            )}
            {allowed('merchants') && (
              <button
                type="button"
                className="shell-btn"
                onClick={exportPortfolio}
                disabled={exporting}
                title={t('shell.reportTitle')}
              >
                <Icon name="download" size={15} />
                <span className="shell-btn-label">{exporting ? t('common.exporting') : t('shell.report')}</span>
              </button>
            )}
          </div>
        </header>

        {exportError && (
          <div className="shell-flash" role="alert">
            {t('shell.reportFailed', { error: exportError })}
            <button type="button" className="shell-icon-btn" aria-label={t('common.hide')} onClick={() => setExportError(null)}>
              <Icon name="close" size={14} />
            </button>
          </div>
        )}

        {/* Clé sur le thème et la langue : les graphiques (canvas) relisent leurs
            couleurs au changement de thème, et un écran remonté recharge ses
            données — donc ses messages d'erreur — dans la nouvelle langue. */}
        <main className="shell-content" id="contenu" key={`${theme}-${lang}`}>
          {children}
        </main>
      </div>
    </div>
  )
}

const COLLAPSED_KEY = 'icc_nav_collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(value: boolean) {
  try {
    localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0')
  } catch {
    /* préférence non mémorisée, sans conséquence */
  }
}

const THEME_OPTIONS: Array<{ value: ThemePreference; labelKey: MessageKey }> = [
  { value: 'light', labelKey: 'theme.light' },
  { value: 'dark', labelKey: 'theme.dark' },
]

function ThemeSwitch({
  preference,
  theme,
  onChange,
  collapsed,
  t,
}: {
  preference: ThemePreference
  theme: 'light' | 'dark'
  onChange: (p: ThemePreference) => void
  collapsed: boolean
  t: TFunction
}) {
  if (collapsed) {
    // Menu rabattu : un seul bouton qui bascule entre clair et sombre.
    const next = theme === 'dark' ? 'light' : 'dark'
    return (
      <button
        type="button"
        className="shell-icon-btn shell-theme-toggle"
        onClick={() => onChange(next)}
        aria-label={next === 'dark' ? t('theme.switchToDark') : t('theme.switchToLight')}
        title={next === 'dark' ? t('theme.darkShort') : t('theme.lightShort')}
      >
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
      </button>
    )
  }
  return (
    <div className="shell-theme" role="radiogroup" aria-label={t('theme.label')}>
      {THEME_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={preference === o.value}
          className="shell-theme-option"
          onClick={() => onChange(o.value)}
          title={t('theme.named', { name: t(o.labelKey).toLowerCase() })}
        >
          <span>{t(o.labelKey)}</span>
        </button>
      ))}
    </div>
  )
}

function NavEntry({
  item,
  active,
  activeSubPath,
  badges,
  collapsed,
  t,
}: {
  item: NavItem
  active: boolean
  activeSubPath: string | undefined
  badges: Partial<Record<BadgeKey, number>>
  collapsed: boolean
  t: TFunction
}) {
  const link = (
    <a
      href={`#/${item.path}`}
      className={`nav-item${active ? ' nav-item-active' : ''}`}
      aria-current={active && !item.subs ? 'page' : undefined}
      aria-label={collapsed ? t(item.labelKey) : undefined}
      onClick={(e) => {
        // Revenir sur l'écran d'accueil du module même si l'ancre est identique.
        if (active && item.subs) {
          e.preventDefault()
          navigate(item.path)
        }
      }}
    >
      <Icon name={item.icon} />
      <span className="nav-item-label">{t(item.labelKey)}</span>
    </a>
  )

  if (collapsed) {
    // Menu rabattu : icône seule, libellé et sous-menus dans un volet au survol ou au focus.
    const pending = item.subs?.some((s) => s.badge && badges[s.badge])
    return (
      <div className="nav-entry">
        {link}
        {pending && <span className="nav-dot" aria-hidden="true" />}
        <div className="nav-flyout">
          <a href={`#/${item.path}`} className="nav-flyout-title">{t(item.labelKey)}</a>
          {item.subs?.map((sub) => {
            const count = sub.badge ? badges[sub.badge] : undefined
            const isActive = active && activeSubPath === sub.path
            return (
              <a key={sub.path} href={`#/${sub.path}`} className={`nav-sub${isActive ? ' nav-sub-active' : ''}`} aria-current={isActive ? 'page' : undefined}>
                <span>{t(sub.labelKey)}</span>
                {count ? <span className={`nav-count${sub.badge === 'betting.qualify' ? ' nav-count-warn' : ''}`}>{count}</span> : null}
              </a>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <>
      {link}
      {active &&
        item.subs?.map((sub) => {
          const count = sub.badge ? badges[sub.badge] : undefined
          const isActive = activeSubPath === sub.path
          return (
            <a
              key={sub.path}
              href={`#/${sub.path}`}
              className={`nav-sub${isActive ? ' nav-sub-active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <span>{t(sub.labelKey)}</span>
              {count ? (
                <span className={`nav-count${sub.badge === 'betting.qualify' ? ' nav-count-warn' : ''}`}>{count}</span>
              ) : null}
            </a>
          )
        })}
    </>
  )
}

export { isManagerRole }
